import Identification20 from '@carbon/icons-react/es/identification/20';
import { SecureRoute } from '@okta/okta-react';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, Switch, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { CostRateSourceType } from 'api/cost-rates';
import { RoleType } from 'api/models';
import { AddButton } from 'components/atoms/button';
import { PageLoading } from 'components/atoms/loading';
import { Switcher } from 'components/atoms/switcher';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { ManageCostRates } from 'components/organisms/module-settings/cost-rates';
import {
  RoleSettingsTab,
  RolesList,
} from 'components/organisms/module-settings/roles';
import { Argon as ArgonTemplate } from 'components/templates';
import { Content } from 'components/templates/argon/components';
import { RoutePaths } from 'constant';
import { RootState } from 'data';
import { useTranslation } from 'hooks/use-translation';
import { useUsersQuery } from 'queries/members';
import { useRolesQuery } from 'queries/roles';
import { unreachable } from 'utils/unreachable';
import { CreateRole } from './create';
import { UpdateRole } from './update';

const ContentWrapper = styled.div`
  padding: 0 var(--spacing-05);
  height: 100%;
`;
const Argon = styled(ArgonTemplate)`
  ${Content} {
    border: none;
  }
`;

export const getRoleTypeForTab = (tab: RoleSettingsTab): RoleType => {
  switch (tab) {
    case RoleSettingsTab.Vendor:
      return RoleType.Vendor;
    case RoleSettingsTab.Customer:
      return RoleType.Customer;
    default:
      return unreachable(tab);
  }
};

export const getTabForRoleType = (type: RoleType): RoleSettingsTab => {
  switch (type) {
    case RoleType.Vendor:
      return RoleSettingsTab.Vendor;
    case RoleType.Customer:
      return RoleSettingsTab.Customer;
    default:
      return unreachable(type);
  }
};

export const RolesSettingsPage: FC = () => {
  const { data: roles = [], isLoading: isFetchingRoles } = useRolesQuery();
  const { data: users = [], isLoading: isFetchingUsers } = useUsersQuery();
  const title = useTranslation('settings.roles.title');
  const history = useHistory();

  const [roleIdForCostRate, setRoleIdForCostRate] =
    useState<number | undefined>(undefined);

  const openCostRateManager = useCallback((id: number) => {
    setRoleIdForCostRate(id);
  }, []);

  const closeCostRateManager = useCallback(() => {
    setRoleIdForCostRate(undefined);
  }, []);

  const goToSettings = useCallback(() => {
    history.push(RoutePaths.rolesSettings);
  }, [history]);

  const { byId: teamsById, ids: teamIds } = useSelector(
    ({ teams }: RootState) => teams,
  );
  const [currentTab, setCurrentTab] = useState<RoleSettingsTab>(
    RoleSettingsTab.Vendor,
  );
  const filteredRoles = useMemo(
    () => roles.filter(({ type }) => type === getRoleTypeForTab(currentTab)),
    [currentTab, roles],
  );
  const pageContentLoading =
    isFetchingRoles || isFetchingUsers || teamIds[0] === 'loading';

  const roleLookup = useMemo(() => {
    if (isFetchingRoles) {
      return undefined;
    }

    return keyBy(roles, 'roleId');
  }, [isFetchingRoles, roles]);

  const userLookup = useMemo(() => {
    if (isFetchingUsers) {
      return undefined;
    }

    return keyBy(users, 'userId');
  }, [isFetchingUsers, users]);

  return (
    <Argon
      title={<HeaderWithIconTitle icon={<Identification20 />} title={title} />}
      documentTitle={title}
      actions={
        pageContentLoading
          ? {}
          : {
              primary: (
                <Switcher
                  activeColor="black"
                  value={currentTab}
                  onChange={(e) => setCurrentTab(e.target.value)}
                >
                  <Switcher.Button value={RoleSettingsTab.Vendor}>
                    <FormattedMessage id="settings.roles.tabs.vendor" />
                  </Switcher.Button>
                  <Switcher.Button value={RoleSettingsTab.Customer}>
                    <FormattedMessage id="settings.roles.tabs.customer" />
                  </Switcher.Button>
                </Switcher>
              ),
              secondary: (
                <Link to={RoutePaths.newRole}>
                  <AddButton size="small">
                    <FormattedMessage id="settings.roles.new" />
                  </AddButton>
                </Link>
              ),
            }
      }
      content={
        pageContentLoading ? (
          <PageLoading css />
        ) : (
          <ContentWrapper>
            <RolesList
              roles={filteredRoles}
              users={users}
              placeholders={teamsById}
              view={currentTab}
              openCostRateManager={openCostRateManager}
            />
            <Switch>
              <SecureRoute path={RoutePaths.newRole}>
                <CreateRole
                  roles={roles}
                  placeholders={teamsById}
                  onClose={goToSettings}
                  view={currentTab}
                  switchTab={setCurrentTab}
                />
              </SecureRoute>
              <SecureRoute path={RoutePaths.editRole}>
                <UpdateRole
                  roles={roles}
                  placeholders={teamsById}
                  onClose={goToSettings}
                  view={currentTab}
                />
              </SecureRoute>
            </Switch>
            {roleIdForCostRate != null &&
              roleLookup != null &&
              userLookup != null && (
                <ManageCostRates
                  sourceType={CostRateSourceType.Role}
                  sourceId={roleIdForCostRate}
                  resourceName={roleLookup[roleIdForCostRate]?.roleName ?? ''}
                  userLookup={userLookup}
                  onClose={closeCostRateManager}
                />
              )}
          </ContentWrapper>
        )
      }
    />
  );
};
